/**
 * pages/AlertConfig.jsx
 * Manage cost threshold alerts per client.
 * Supports both per-service and overall total alerts.
 */
import PrereqBanner from "../components/PrereqBanner";
import { useState, useEffect } from "react";
import { alerts as alertsApi, catalogue as catApi } from "../api/client";
import {
  Card, Btn, SectionTitle, Badge, ErrorBanner,
  SuccessBanner, Spinner, Empty, Table, TR, TD,
} from "../components/UI";

const fmtINR = n => `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESET_THRESHOLDS = [
  { label: "70%", value: 70 },
  { label: "80%", value: 80 },
  { label: "85%", value: 85 },
  { label: "90%", value: 90 },
  { label: "95%", value: 95 },
];

export default function AlertConfig({ clientId, clientName }) {
  const [configs, setConfigs]     = useState([]);
  const [history, setHistory]     = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [checking, setChecking]   = useState(false);
  const [showForm, setShowForm]   = useState(false);

  const [form, setForm] = useState({
    service_id: "",
    alert_name: "",
    threshold_pct: 85,
    alert_emails: "",
    is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, cat] = await Promise.all([
        alertsApi.list(clientId),
        alertsApi.history(clientId),
        catApi.list(),
      ]);
      setConfigs(c); setHistory(h); setCatalogue(cat);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [clientId]);

  const save = async () => {
    if (!form.alert_name.trim()) { setError("Alert name is required"); return; }
    if (!form.alert_emails.trim()) { setError("At least one email is required"); return; }
    setSaving(true); setError("");
    try {
      await alertsApi.create(clientId, {
        ...form,
        service_id: form.service_id || null,
        threshold_pct: Number(form.threshold_pct),
      });
      setSuccess("Alert created successfully");
      setShowForm(false);
      setForm({ service_id: "", alert_name: "", threshold_pct: 85, alert_emails: "", is_active: true });
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const toggle = async (a) => {
    try {
      await alertsApi.update(clientId, a.id, { is_active: !a.is_active });
      await load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this alert?")) return;
    try { await alertsApi.delete(clientId, aid); await load(); }
    catch (e) { setError(e.message); }
  };

  const checkNow = async () => {
    setChecking(true); setError("");
    try {
      const r = await alertsApi.check(clientId);
      setSuccess(`Alert check complete — ${r.stats.triggered} triggered, ${r.stats.checked} checked`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setChecking(false); }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 48 }}><Spinner size={28} /></div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <SectionTitle
          children="Cost threshold alerts"
          sub={`Configure email alerts when ${clientName}'s costs approach the RFP budget — per service or overall`}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={checkNow} loading={checking} variant="secondary" size="sm">
            ⚡ Check now
          </Btn>
          <Btn onClick={() => setShowForm(!showForm)} size="sm">
            + New alert
          </Btn>
        </div>
      </div>

      <ErrorBanner   message={error}   onDismiss={() => setError("")} />
      <SuccessBanner message={success} onDismiss={() => setSuccess("")} />

      {/* How it works */}
      <Card style={{ marginBottom: 20, background: "#F0F9FF", border: "1px solid #BAE6FD" }}>
        <div style={{ fontSize: 13, color: "#0C4A6E", lineHeight: 1.8 }}>
          <strong>How alerts work:</strong> Each alert monitors actual OCI costs against the RFP planned budget.
          When actual cost reaches the configured % threshold, an email is sent via OCI Notifications.
          Alerts fire <strong>daily</strong> (every morning) for as long as the threshold remains exceeded during the month.
        </div>
      </Card>

      {/* New alert form */}
      {showForm && (
        <Card style={{ marginBottom: 20, border: "2px dashed #2563EB", background: "#EFF6FF" }}>
          <div style={{ fontWeight: 700, color: "#1D4ED8", marginBottom: 16, fontSize: 15 }}>
            New alert configuration
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Alert name</label>
              <input value={form.alert_name} onChange={e => setForm({ ...form, alert_name: e.target.value })}
                placeholder="e.g. WAF overspend alert" style={inp} />
            </div>
            <div>
              <label style={lbl}>Scope</label>
              <select value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value })} style={sel}>
                <option value="">Overall total (all services combined)</option>
                <optgroup label="── Per service ──">
                  {catalogue.map(c => (
                    <option key={c.service_id} value={c.service_id}>
                      {c.service} — {c.name}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Threshold %</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {PRESET_THRESHOLDS.map(p => (
                  <button key={p.value} onClick={() => setForm({ ...form, threshold_pct: p.value })}
                    style={{
                      padding: "4px 12px", borderRadius: 6, border: "1px solid",
                      cursor: "pointer", fontSize: 12, fontWeight: 600,
                      borderColor: form.threshold_pct === p.value ? "#2563EB" : "#E5E7EB",
                      background: form.threshold_pct === p.value ? "#2563EB" : "#fff",
                      color: form.threshold_pct === p.value ? "#fff" : "#374151",
                    }}>
                    {p.label}
                  </button>
                ))}
              </div>
              <input type="number" min="1" max="100" value={form.threshold_pct}
                onChange={e => setForm({ ...form, threshold_pct: Number(e.target.value) })}
                style={{ ...inp, width: 100 }} />
              <span style={{ fontSize: 12, color: "#6B7280", marginLeft: 8 }}>
                % of planned budget
              </span>
            </div>
            <div>
              <label style={lbl}>Alert email(s)</label>
              <input value={form.alert_emails} onChange={e => setForm({ ...form, alert_emails: e.target.value })}
                placeholder="email1@domain.com, email2@domain.com" style={inp} />
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                Separate multiple emails with commas
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn onClick={() => setShowForm(false)} variant="secondary" size="sm">Cancel</Btn>
            <Btn onClick={save} loading={saving}>Save alert</Btn>
          </div>
        </Card>
      )}

      {/* Alert list */}
      {configs.length === 0
        ? <Empty icon="🔔" title="No alerts configured" sub="Click '+ New alert' to set up your first threshold alert" />
        : (
          <Table headers={["Alert name", "Scope", "Threshold", "Email(s)", "Status", "Actions"]} style={{ marginBottom: 24 }}>
            {configs.map((a, i) => (
              <TR key={a.id} i={i}>
                <TD bold>{a.alert_name}</TD>
                <TD style={{ color: "#6B7280", fontSize: 12 }}>
                  {a.service_id
                    ? catalogue.find(c => c.service_id === a.service_id)?.service || a.service_id
                    : <span style={{ color: "#7C3AED", fontWeight: 600 }}>Overall total</span>
                  }
                </TD>
                <TD right>
                  <span style={{
                    fontWeight: 700, fontSize: 15,
                    color: a.threshold_pct >= 90 ? "#DC2626" : a.threshold_pct >= 80 ? "#D97706" : "#16A34A",
                  }}>
                    {a.threshold_pct}%
                  </span>
                </TD>
                <TD style={{ fontSize: 12, color: "#4B5563", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {a.alert_emails}
                </TD>
                <TD>
                  <Badge color={a.is_active ? "green" : "gray"}>
                    {a.is_active ? "Active" : "Paused"}
                  </Badge>
                </TD>
                <TD>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn onClick={() => toggle(a)} variant="secondary" size="sm">
                      {a.is_active ? "Pause" : "Resume"}
                    </Btn>
                    <Btn onClick={() => remove(a.id)} variant="danger" size="sm">✕</Btn>
                  </div>
                </TD>
              </TR>
            ))}
          </Table>
        )
      }

      {/* Alert history */}
      {history.length > 0 && (
        <Card>
          <SectionTitle children="Recent alerts sent" sub="Last 100 notifications sent for this client" />
          <Table headers={["Sent at", "Month", "Actual cost", "Planned", "Reached %", "Notification ID"]}>
            {history.slice(0, 20).map((h, i) => (
              <TR key={h.id} i={i}>
                <TD style={{ fontSize: 12 }}>{new Date(h.sent_at).toLocaleString("en-IN")}</TD>
                <TD bold>{h.month}</TD>
                <TD right color="#DC2626" bold>{fmtINR(h.actual_cost)}</TD>
                <TD right color="#4B5563">{fmtINR(h.planned_cost)}</TD>
                <TD right bold color={h.reached_pct >= 90 ? "#DC2626" : "#D97706"}>
                  {h.reached_pct.toFixed(1)}%
                </TD>
                <TD style={{ fontSize: 11, color: "#9CA3AF" }}>
                  {h.notification_id ? h.notification_id.slice(0, 20) + "..." : "—"}
                </TD>
              </TR>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 };
const inp = { width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box" };
const sel = { width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, background: "#fff", cursor: "pointer" };
